// Portfolio Administration v1.0 PR 4a §7.7 — Vendors tab (per-loan view).
//   • Per-loan vendor "assignments" are LoanPartyAssignment rows whose
//     counterpartyType=Vendor; vendor names link to the vendor record. The
//     Assign-vendor Role dropdown is seeded from the vendor-type catalog.
//   • The VFI (Vendor Fee Income) card is the real per-loan billing summary
//     (Vendor Billing v1.0) — open/overdue/paid-YTD chips + open invoices
//     deep-linking to the vendor's VFI Ledger tab filtered to this loan.
//   (The required-vendor compliance matrix + its 60-day covenant automation
//    were retired.)

import { useMemo, useState } from "react";
import { Plus } from "lucide-react";
import { Button, Card } from "@/design-system/components/ui";
import { useAuth } from "@/hooks/useAuth";
import {
  useLoanParties,
  useLoanVendorConnections,
  useRequiredVendorCatalog,
  useVendors,
} from "@/hooks/portfolioAdmin/usePortfolioAdmin";
import { ActiveVendorAssignmentsTable } from "../vendors/ActiveVendorAssignmentsTable";
import { VendorAssignmentDrawer } from "../vendors/VendorAssignmentDrawer";
import { AssignVendorModal } from "../vendors/AssignVendorModal";
import { LoanVfiSummaryCard } from "../vendors/LoanVfiSummaryCard";
import { LoanVendorHistoryCard } from "../vendors/LoanVendorHistoryCard";
import { LoanVendorReportsCard } from "../vendors/LoanVendorReportsCard";
import type { LoanSummaryDto } from "@/api/portfolioAdmin/types";

interface VendorsTabProps {
  loan: LoanSummaryDto;
}

export function VendorsTab({ loan }: VendorsTabProps) {
  const auth = useAuth();
  // Mirrors the server gate on POST loans/{id}/parties for a Vendor party:
  // Vendors.Manage (E1-VM / E2-VM / E5) OR Loans.Manage — the old role-code
  // check showed the button to roles whose request then 403'd.
  const canAssign =
    auth.hasPermission("Vendors.Manage") || auth.hasPermission("Loans.Manage");

  const partiesQuery = useLoanParties(loan.id);
  const vendorsQuery = useVendors();
  const catalogQuery = useRequiredVendorCatalog();

  const vendorParties = useMemo(
    () =>
      (partiesQuery.data ?? []).filter(
        (p) => p.counterpartyType === "Vendor" && p.isActive,
      ),
    [partiesQuery.data],
  );
  // Step 5 W1 — the existing loan-scoped connections read, reused. No new
  // plumbing: this is the same query the assignment drawer already runs.
  const connectionsQuery = useLoanVendorConnections(loan.id);
  const connectedAssignmentIds = useMemo(
    () =>
      connectionsQuery.data === undefined
        ? undefined
        : new Set(
            connectionsQuery.data
              .filter((c) => c.isLive)
              .map((c) => c.partyAssignmentId),
          ),
    [connectionsQuery.data],
  );
  // Only ACTIVE assignments can be connected, and vendorParties is already
  // filtered to active — an ended assignment is not an outstanding task.
  const unconnectedCount = useMemo(
    () =>
      connectedAssignmentIds === undefined
        ? 0
        : vendorParties.filter((p) => !connectedAssignmentIds.has(p.id)).length,
    [connectedAssignmentIds, vendorParties],
  );

  const registry = vendorsQuery.data ?? [];
  const registryById = useMemo(
    () => new Map(registry.map((v) => [v.id, v])),
    [registry],
  );
  // Assign-modal role options follow the served vendor-type catalog (plus
  // "Other") so new assignments match the seeded VendorType strings exactly.
  const roleOptions = useMemo(() => {
    const types = (catalogQuery.data ?? []).map((c) => c.vendorType);
    return [...types, "Other"];
  }, [catalogQuery.data]);

  const [openAssignmentId, setOpenAssignmentId] = useState<string | null>(null);
  const [assignOpen, setAssignOpen] = useState(false);

  const openAssignment = openAssignmentId
    ? vendorParties.find((p) => p.id === openAssignmentId) ?? null
    : null;

  if (partiesQuery.isLoading) {
    return (
      <Card>
        <p className="text-small text-gray-mid italic m-0">Loading vendors…</p>
      </Card>
    );
  }
  if (partiesQuery.isError) {
    return (
      <Card>
        <p className="text-small text-status-error m-0">
          Could not load vendor assignments. Refresh to retry.
        </p>
      </Card>
    );
  }

  return (
    <div className="flex flex-col gap-space-4" data-testid="vendors-tab">
      <Card>
        <div className="flex items-center justify-between gap-space-3 flex-wrap mb-space-3">
          <h3 className="font-display font-bold text-h3 m-0">
            Active vendor assignments
          </h3>
          {canAssign && (
            <Button
              variant="primary"
              size="sm"
              onClick={() => setAssignOpen(true)}
              // Role options come from the served required-vendor catalog;
              // opening the modal before they load (or after an error) would
              // collapse the Role select to "Other" — an assignment the
              // covenant matcher can never match to its slot.
              disabled={!catalogQuery.data}
              title={catalogQuery.data ? undefined : "Loading vendor roles…"}
              data-testid="vendor-add-button"
            >
              <Plus size={14} aria-hidden /> Assign vendor
            </Button>
          )}
        </div>
        {unconnectedCount > 0 && (
          // Step 5 W1 — the one place staff learn that an assignment never
          // became a connection. Being assigned to a loan does not open the
          // connection; a separate action in the row drawer does. Before
          // this, the only hint lived inside that drawer — invisible unless
          // you had already opened the exact row — so the gap was silent on
          // both sides: the vendor saw an empty Customers tab and staff saw
          // a row marked "Active".
          <p
            className="text-small text-status-warning m-0 mb-space-3"
            data-testid="vendors-unconnected-prompt"
          >
            {unconnectedCount === 1
              ? "1 active assignment has no customer connection — that vendor cannot see the customer in their portal. Open the row to make the connection."
              : `${unconnectedCount} active assignments have no customer connection — those vendors cannot see the customer in their portal. Open a row to make the connection.`}
          </p>
        )}
        <ActiveVendorAssignmentsTable
          vendorParties={vendorParties}
          vendorRegistry={registry}
          onRowClick={(a) => setOpenAssignmentId(a.id)}
          connectedAssignmentIds={connectedAssignmentIds}
        />
      </Card>

      <LoanVfiSummaryCard loanId={loan.id} />

      <LoanVendorReportsCard loanId={loan.id} />

      <LoanVendorHistoryCard loanId={loan.id} />

      {openAssignment && (
        <VendorAssignmentDrawer
          loanId={loan.id}
          loanNumber={loan.loanNumber}
          assignment={openAssignment}
          vendor={registryById.get(openAssignment.counterpartyId)}
          open={!!openAssignment}
          onClose={() => setOpenAssignmentId(null)}
        />
      )}
      {assignOpen && (
        <AssignVendorModal
          loanId={loan.id}
          open={assignOpen}
          onClose={() => setAssignOpen(false)}
          roleOptions={roleOptions}
        />
      )}
    </div>
  );
}
